import { IconColumns } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";

type ColumnsData = {
  id?: number;
  text?: string;
  isShow?: boolean;
  value?: string;
};

type Props = {
  columnsData: ColumnsData[];
  setHeadings?: (value: string | undefined, checked: boolean) => void;
  triggerClassName?: string;
};

export default function ColumnsFilter({
  columnsData,
  setHeadings,
  triggerClassName,
}: Props) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allSelected = columnsData.every((col) => col.isShow);
  const someSelected = columnsData.some((col) => col.isShow);

  const handleSelectAll = (checked: boolean) => {
    columnsData.forEach((col) => {
      setHeadings?.(col.value, checked);
    });
  };

  const CheckIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3 w-3"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={
          triggerClassName ||
          "border border-slate-200 bg-white rounded-lg p-1.5 cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
        }
      >
        <IconColumns strokeWidth={1.5} size={18} />
      </button>

      {/* Dropdown panel — end-0 handles both LTR (right-align) and RTL (left-align) */}
      {open && (
        <div
          className="absolute top-full mt-2 end-0 bg-white border border-slate-200 shadow-2xl rounded-2xl w-60 z-[9999] flex flex-col overflow-hidden"
          style={{ maxHeight: "340px" }}
        >
          {/* Header */}
          <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex-shrink-0">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
              {t("columns")}
            </span>
          </div>

          {/* Select All */}
          <label className="flex items-center gap-3 px-4 py-3 hover:bg-[#FBD037]/10 transition-colors cursor-pointer select-none border-b border-slate-100 flex-shrink-0 group">
            <div className="relative flex-shrink-0">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = someSelected && !allSelected;
                }}
                className="peer h-4 w-4 cursor-pointer transition-all appearance-none rounded border border-slate-300 checked:bg-[#FBD037] checked:border-[#FBD037] hover:border-yellow-400 focus:outline-none"
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <span className="absolute text-[#131327] opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <CheckIcon />
              </span>
              <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2.5 h-0.5 bg-[#131327] opacity-0 peer-indeterminate:opacity-100 pointer-events-none" />
            </div>
            <span className="text-xs font-bold text-[#212121] group-hover:text-[#856608] transition-colors">
              {t("Select All")}
            </span>
          </label>

          {/* Column rows — scrollable */}
          <div className="overflow-y-auto flex-1">
            {columnsData.map((col, i) => (
              <label
                key={i}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FBD037]/10 transition-colors cursor-pointer select-none border-b border-slate-50 last:border-none group"
              >
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={!!col.isShow}
                    className="peer h-4 w-4 cursor-pointer transition-all appearance-none rounded border border-slate-300 checked:bg-[#FBD037] checked:border-[#FBD037] hover:border-yellow-400 focus:outline-none"
                    onChange={(e) => {
                      setHeadings?.(col.value, e.target.checked);
                    }}
                  />
                  <span className="absolute text-[#131327] opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    <CheckIcon />
                  </span>
                </div>
                <span className="text-xs text-slate-600 font-medium leading-tight group-hover:text-[#856608] transition-colors">
                  {t(col.text || "")}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
